import { supabase } from '../lib/supabase';
import { vehicleHistoryService } from './vehicleHistoryService';

const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

/**
 * Record a timeline event for a work order
 */
export const recordTimelineEvent = async ({
  workOrderId,
  stage,
  eventType,
  description,
  metadata = {},
  userId = null,
}) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    const resolvedUserId = userId || user?.id || null;

    const { data, error } = await supabase?.from('timeline_events')?.insert({
      work_order_id: workOrderId,
      stage: stage,
      status: eventType,
      event_type: eventType,
      description: description,
      metadata: metadata,
      responsible_person: resolvedUserId,
      timestamp: new Date()?.toISOString(),
    })?.select()?.single();

    if (error) {
      console.warn('[quotationFlowService] Timeline event insert error:', error?.message);
      return null;
    }
    return toCamelCase(data);
  } catch (err) {
    console.warn('[quotationFlowService] recordTimelineEvent failed:', err?.message);
    return null;
  }
};

/**
 * Send quotation via WhatsApp or Email
 * - Updates quotation status to 'enviada'
 * - Saves sent_at, sent_via, sent_to
 * - Records timeline event
 * - Auto-creates vehicle_history PENDING entry if quote has items
 */
export const sendQuotation = async ({
  quotationId,
  workOrderId,
  via, // 'whatsapp' | 'email' | 'both'
  sentTo, // phone number or email
  messageContent = '',
  vehicleId = null,
  quoteItems = [],
}) => {
  try {
    const now = new Date()?.toISOString();

    // Update quotation
    const { data: updatedQuote, error: updateErr } = await supabase
      ?.from('quotations')
      ?.update({
        status: 'enviada',
        sent_at: now,
        sent_via: via,
        sent_to: sentTo,
        updated_at: now,
      })
      ?.eq('id', quotationId)
      ?.select()
      ?.single();

    if (updateErr) {
      console.error('[quotationFlowService] sendQuotation update error:', updateErr?.message);
      throw new Error(updateErr?.message);
    }

    // Record timeline event
    await recordTimelineEvent({
      workOrderId,
      stage: 'cotizacion',
      eventType: 'Quote Sent',
      description: `Cotización enviada por ${via === 'whatsapp' ? 'WhatsApp' : via === 'email' ? 'Email' : 'WhatsApp y Email'} a ${sentTo}`,
      metadata: { via, sentTo, sentAt: now },
    });

    // Auto-create vehicle_history PENDING entry if quote has items and vehicleId is known
    if (vehicleId && quoteItems?.length > 0) {
      await vehicleHistoryService?.autoCreateFromQuote({
        vehicleId,
        quoteId: quotationId,
        title: `Cotización enviada con ${quoteItems?.length} partida(s) pendiente(s)`,
      });
    }

    return toCamelCase(updatedQuote);
  } catch (err) {
    console.error('[quotationFlowService] sendQuotation failed:', err);
    throw err;
  }
};

/**
 * Approve a quotation and automatically create a Work Order
 * - Sets quote.status = 'aprobada', quote.approved_at
 * - Creates work_order with scope_of_work from quote items
 * - Records timeline events: Quote Approved + Work Order Created
 * - Auto-creates vehicle_history DONE entry
 */
export const approveQuotationAndCreateWorkOrder = async ({
  quotationId,
  workOrderId,
  quoteItems = [],
  inspectionFindings = [],
  approvedBy = null,
}) => {
  try {
    const now = new Date()?.toISOString();
    const { data: { user } } = await supabase?.auth?.getUser();
    const resolvedUserId = approvedBy || user?.id || null;

    // 1. Fetch full quotation to get linked IDs
    const { data: quote } = await supabase
      ?.from('quotations')
      ?.select('*, work_order:work_orders(id, folio_ot, vehicle_id, client_id, company_id, inspection_id)')
      ?.eq('id', quotationId)
      ?.maybeSingle();

    const resolvedWorkOrderId = workOrderId || quote?.work_order_id;

    // 2. Update quotation status to approved
    const { data: updatedQuote, error: quoteErr } = await supabase
      ?.from('quotations')
      ?.update({
        status: 'aprobada',
        approved_at: now,
        items_snapshot: quoteItems,
        updated_at: now,
      })
      ?.eq('id', quotationId)
      ?.select()
      ?.single();

    if (quoteErr) {
      console.error('[quotationFlowService] approveQuotation update error:', quoteErr?.message);
      throw new Error(quoteErr?.message);
    }

    // 3. Build scope_of_work from authorized items + inspection findings
    const scopeOfWork = [
      ...quoteItems?.map(item => ({
        type: item?.type || 'service',
        description: item?.description,
        quantity: item?.quantity || 1,
        price: item?.price || 0,
        supplier: item?.supplier || '',
        authorized: true,
      })),
      ...inspectionFindings?.map(finding => ({
        type: 'finding',
        description: finding?.description || finding?.findings || '',
        category: finding?.category || '',
        priority: finding?.priority || 'medium',
        authorized: false,
      })),
    ];

    // 4. Update work_order: set quote_id, scope_of_work, work_status, estado_ot
    const { data: updatedWO, error: woErr } = await supabase
      ?.from('work_orders')
      ?.update({
        quote_id: quotationId,
        scope_of_work: scopeOfWork,
        work_status: 'created',
        estado_ot: 'aprobado',
        updated_at: now,
      })
      ?.eq('id', resolvedWorkOrderId)
      ?.select(`
        *,
        vehicle:vehicles(id, marca, modelo, year, vin, placas, mileage, kilometraje),
        client:clients(id, nombre, telefono, email)
      `)
      ?.single();

    if (woErr) {
      console.error('[quotationFlowService] work_order update error:', woErr?.message);
      throw new Error(woErr?.message);
    }

    // 5. Auto-create vehicle_history DONE entry for this Work Order
    const vehicleId = updatedWO?.vehicle_id || updatedWO?.vehicle?.id || quote?.work_order?.vehicle_id;
    const vehicleMileage = updatedWO?.vehicle?.mileage || updatedWO?.vehicle?.kilometraje || null;

    if (vehicleId) {
      await vehicleHistoryService?.autoCreateFromWorkOrder({
        vehicleId,
        workOrderId: resolvedWorkOrderId,
        vehicleMileage,
      });

      // Apply preventive service rules to generate next PENDING entries
      await vehicleHistoryService?.applyPreventiveRules({
        vehicleId,
        workOrderId: resolvedWorkOrderId,
        serviceKmAtTime: vehicleMileage,
      });
    }

    // 6. Record timeline events
    await recordTimelineEvent({
      workOrderId: resolvedWorkOrderId,
      stage: 'aprobacion',
      eventType: 'Quote Approved',
      description: 'Cotización aprobada por el cliente',
      metadata: { quotationId, approvedAt: now, approvedBy: resolvedUserId },
      userId: resolvedUserId,
    });

    await recordTimelineEvent({
      workOrderId: resolvedWorkOrderId,
      stage: 'ejecucion',
      eventType: 'Work Order Created',
      description: 'Orden de trabajo creada y enviada a Taller',
      metadata: { workOrderId: resolvedWorkOrderId, scopeItems: scopeOfWork?.length },
      userId: resolvedUserId,
    });

    return {
      quotation: toCamelCase(updatedQuote),
      workOrder: toCamelCase(updatedWO),
    };
  } catch (err) {
    console.error('[quotationFlowService] approveQuotationAndCreateWorkOrder failed:', err);
    throw err;
  }
};

/**
 * Reject a quotation
 * - Sets quote.status = 'rechazada', quote.rejected_at, quote.rejection_reason
 * - Records timeline event
 */
export const rejectQuotation = async ({
  quotationId,
  workOrderId,
  reason = '',
}) => {
  try {
    const now = new Date()?.toISOString();

    const { data: updatedQuote, error } = await supabase
      ?.from('quotations')
      ?.update({
        status: 'rechazada',
        rejected_at: now,
        rejection_reason: reason,
        updated_at: now,
      })
      ?.eq('id', quotationId)
      ?.select()
      ?.single();

    if (error) throw new Error(error?.message);

    await recordTimelineEvent({
      workOrderId,
      stage: 'cotizacion',
      eventType: 'Quote Rejected',
      description: `Cotización rechazada${reason ? ': ' + reason : ''}`,
      metadata: { quotationId, rejectedAt: now, reason },
    });

    return toCamelCase(updatedQuote);
  } catch (err) {
    console.error('[quotationFlowService] rejectQuotation failed:', err);
    throw err;
  }
};

/**
 * Assign mechanic to a work order
 * - Sets assigned_to_user_id, assigned_at, work_status = 'assigned'
 * - Records timeline event
 */
export const assignMechanic = async ({
  workOrderId,
  mechanicUserId,
  mechanicName = '',
}) => {
  try {
    const now = new Date()?.toISOString();

    const { data: updatedWO, error } = await supabase
      ?.from('work_orders')
      ?.update({
        assigned_to_user_id: mechanicUserId,
        assigned_to: mechanicUserId,
        assigned_at: now,
        work_status: 'assigned',
        updated_at: now,
      })
      ?.eq('id', workOrderId)
      ?.select()
      ?.single();

    if (error) throw new Error(error?.message);

    await recordTimelineEvent({
      workOrderId,
      stage: 'ejecucion',
      eventType: 'Assigned to Mechanic',
      description: `OT asignada a mecánico${mechanicName ? ': ' + mechanicName : ''}`,
      metadata: { mechanicUserId, mechanicName, assignedAt: now },
    });

    return toCamelCase(updatedWO);
  } catch (err) {
    console.error('[quotationFlowService] assignMechanic failed:', err);
    throw err;
  }
};

/**
 * Update work order execution status (in_progress / completed)
 */
export const updateWorkOrderStatus = async ({
  workOrderId,
  newStatus, // 'in_progress' | 'completed' | 'delivered'
  notes = '',
}) => {
  try {
    const now = new Date()?.toISOString();
    const updatePayload = {
      work_status: newStatus,
      updated_at: now,
    };

    if (newStatus === 'in_progress') {
      updatePayload.started_at = now;
      updatePayload.estado_ot = 'enproceso';
    } else if (newStatus === 'completed') {
      updatePayload.completed_at = now;
      updatePayload.estado_ot = 'terminado';
    } else if (newStatus === 'delivered') {
      updatePayload.estado_ot = 'entregado';
    }

    const { data: updatedWO, error } = await supabase
      ?.from('work_orders')
      ?.update(updatePayload)
      ?.eq('id', workOrderId)
      ?.select()
      ?.single();

    if (error) throw new Error(error?.message);

    const eventDescriptions = {
      in_progress: 'Trabajo iniciado en taller',
      completed: 'Trabajo completado en taller',
      delivered: 'Vehículo entregado al cliente',
    };

    await recordTimelineEvent({
      workOrderId,
      stage: 'ejecucion',
      eventType: newStatus === 'in_progress' ? 'Work Started' : newStatus === 'completed' ? 'Work Completed' : 'Vehicle Delivered',
      description: eventDescriptions?.[newStatus] || `Estado actualizado a ${newStatus}`,
      metadata: { newStatus, notes, timestamp: now },
    });

    // Auto-create vehicle history record when work order is completed
    if (newStatus === 'completed') {
      try {
        // Fetch full work order with vehicle, client, and quotation data
        const { data: wo, error: woFetchErr } = await supabase
          ?.from('work_orders')
          ?.select(`
            id, folio_ot, completed_at, notas, kilometraje, vehicle_id, client_id, quote_id,
            vehicles(*),
            clients(*),
            quotation:quotations(authorized_items_snapshot, items_snapshot, total)
          `)
          ?.eq('id', workOrderId)
          ?.single();

        if (woFetchErr) {
          console.warn('[VEHICLE_HISTORY] Failed to fetch work order data:', woFetchErr?.message);
        } else if (wo) {
          // Get items from authorized_items_snapshot or fallback to items_snapshot
          const items = wo?.quotation?.authorized_items_snapshot || wo?.quotation?.items_snapshot || [];

          // Separate parts and labor
          const parts = items?.filter(i => i?.type === 'part');
          const labor = items?.filter(i => i?.type === 'labor');

          // Build snapshots
          const vehicle_snapshot = {
            marca: wo?.vehicles?.marca || '',
            modelo: wo?.vehicles?.modelo || '',
            year: wo?.vehicles?.year || '',
            vin: wo?.vehicles?.vin || '',
            placas: wo?.vehicles?.placas || '',
            color: wo?.vehicles?.color || '',
            motor: wo?.vehicles?.motor || '',
          };

          const client_snapshot = {
            nombre: wo?.clients?.nombre || '',
            telefono: wo?.clients?.telefono || '',
            email: wo?.clients?.email || '',
          };

          const parts_snapshot = parts?.map(p => ({
            description: p?.description || '',
            category: p?.category || '',
            supplier: p?.supplier || '',
            quantity: p?.quantity || 1,
            price: p?.price || 0,
          }));

          const labor_snapshot = labor?.map(l => ({
            description: l?.description || '',
            hours: l?.quantity || 0,
          }));

          // Get company_id from auth user metadata if available
          const { data: { user } } = await supabase?.auth?.getUser();
          const company_id = user?.user_metadata?.company_id || null;

          // Upsert into vehicle_history (onConflict work_order_id prevents duplicates)
          const { error: histErr } = await supabase
            ?.from('vehicle_history')
            ?.upsert({
              vehicle_id: wo?.vehicle_id,
              work_order_id: workOrderId,
              type: 'DONE',
              status: 'CLOSED',
              source_type: 'WORK_ORDER',
              source_id: workOrderId,
              is_auto_generated: true,
              title: `Servicio completado — ${wo?.folio_ot}`,
              order_number: wo?.folio_ot,
              service_date: wo?.completed_at || now,
              notes: wo?.notas || '',
              parts_snapshot,
              labor_snapshot,
              client_snapshot,
              vehicle_snapshot,
              ...(company_id ? { company_id } : {}),
            }, { onConflict: 'work_order_id', ignoreDuplicates: true });

          if (histErr) {
            console.warn('[VEHICLE_HISTORY] Upsert error:', histErr?.message);
          } else {
            console.log('[VEHICLE_HISTORY_CREATED]', workOrderId, parts?.length, labor?.length);
          }
        }
      } catch (histCreateErr) {
        // Non-blocking: log but don't fail the status update
        console.warn('[VEHICLE_HISTORY] Auto-create failed (non-blocking):', histCreateErr?.message);
      }
    }

    return toCamelCase(updatedWO);
  } catch (err) {
    console.error('[quotationFlowService] updateWorkOrderStatus failed:', err);
    throw err;
  }
};

/**
 * Get work orders for Taller view (created/assigned/in_progress)
 */
export const getTallerWorkOrders = async () => {
  try {
    const { data, error } = await supabase
      ?.from('work_orders')
      ?.select(`
        *,
        vehicle:vehicles(id, marca, modelo, year, vin, placas, color, motor),
        client:clients(id, nombre, telefono, email),
        assigned_mechanic:user_profiles!work_orders_assigned_to_user_id_fkey(id, full_name, email)
      `)
      ?.in('work_status', ['created', 'assigned', 'in_progress'])
      ?.order('fecha_ingreso', { ascending: false });

    if (error) {
      // Fallback without the FK hint if the FK name doesn't match
      const { data: fallback, error: fallbackErr } = await supabase
        ?.from('work_orders')
        ?.select(`
          *,
          vehicle:vehicles(id, marca, modelo, year, vin, placas, color, motor),
          client:clients(id, nombre, telefono, email)
        `)
        ?.in('work_status', ['created', 'assigned', 'in_progress'])
        ?.order('fecha_ingreso', { ascending: false });

      if (fallbackErr) {
        console.error('[quotationFlowService] getTallerWorkOrders error:', fallbackErr?.message);
        return [];
      }
      return (fallback || [])?.map(toCamelCase);
    }

    return (data || [])?.map(toCamelCase);
  } catch (err) {
    console.error('[quotationFlowService] getTallerWorkOrders failed:', err);
    return [];
  }
};

/**
 * Get mechanics (users with role 'taller')
 */
export const getMechanics = async () => {
  try {
    const { data, error } = await supabase
      ?.from('user_profiles')
      ?.select('id, full_name, email, role')
      ?.eq('role', 'taller')
      ?.eq('is_active', true)
      ?.order('full_name');

    if (error) {
      console.warn('[quotationFlowService] getMechanics error:', error?.message);
      return [];
    }
    return (data || [])?.map(toCamelCase);
  } catch (err) {
    console.warn('[quotationFlowService] getMechanics failed:', err);
    return [];
  }
};

/**
 * Build WhatsApp message content for a quotation
 */
export const buildWhatsAppMessage = ({
  folioCotizacion,
  folioRecepcion,
  clientName,
  vehicle,
  quoteItems = [],
  total = 0,
  authUrl = '',
}) => {
  const itemLines = quoteItems
    ?.map(item => `  • ${item?.description} (x${item?.quantity || 1}) — $${((item?.price || 0) * (item?.quantity || 1))?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`)
    ?.join('\n');

  return [
    `🔧 *COTIZACIÓN ${folioCotizacion || ''}*`,
    `📋 Recepción/Inspección: ${folioRecepcion || ''}`,
    ``,
    `👤 Cliente: ${clientName || ''}`,
    `🚗 Vehículo: ${vehicle?.marca || ''} ${vehicle?.modelo || ''} ${vehicle?.year || ''}`,
    vehicle?.vin ? `🔑 VIN: ${vehicle?.vin}` : '',
    vehicle?.placas ? `🪪 Placas: ${vehicle?.placas}` : '',
    ``,
    `📦 *Trabajos / Refacciones:*`,
    itemLines || '  (sin partidas)',
    ``,
    `💰 *Total: $${total?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN*`,
    ``,
    authUrl ? `✅ Para AUTORIZAR o RECHAZAR su cotización:\n${authUrl}` : '',
  ]?.filter(line => line !== '')?.join('\n');
};

/**
 * Build Email body for a quotation
 */
export const buildEmailBody = ({
  folioCotizacion,
  folioRecepcion,
  clientName,
  vehicle,
  quoteItems = [],
  total = 0,
  authUrl = '',
}) => {
  const itemRows = quoteItems
    ?.map(item => `<tr><td style="padding:4px 8px">${item?.description}</td><td style="padding:4px 8px;text-align:center">${item?.quantity || 1}</td><td style="padding:4px 8px;text-align:right">$${((item?.price || 0) * (item?.quantity || 1))?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</td></tr>`)
    ?.join('');

  return `
<div style="font-family:sans-serif;max-width:600px;margin:0 auto">
  <h2 style="color:#1a1a2e">Cotización ${folioCotizacion || ''}</h2>
  <p><strong>Recepción/Inspección:</strong> ${folioRecepcion || ''}</p>
  <p><strong>Cliente:</strong> ${clientName || ''}</p>
  <p><strong>Vehículo:</strong> ${vehicle?.marca || ''} ${vehicle?.modelo || ''} ${vehicle?.year || ''}</p>
  ${vehicle?.vin ? `<p><strong>VIN:</strong> ${vehicle?.vin}</p>` : ''}
  ${vehicle?.placas ? `<p><strong>Placas:</strong> ${vehicle?.placas}</p>` : ''}
  <h3>Detalle de Trabajos</h3>
  <table style="width:100%;border-collapse:collapse;border:1px solid #ddd">
    <thead><tr style="background:#f5f5f5"><th style="padding:8px">Descripción</th><th style="padding:8px">Cant.</th><th style="padding:8px">Total</th></tr></thead>
    <tbody>${itemRows}</tbody>
    <tfoot><tr style="font-weight:bold"><td colspan="2" style="padding:8px">TOTAL</td><td style="padding:8px;text-align:right">$${total?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN</td></tr></tfoot>
  </table>
  ${authUrl ? `<div style="margin-top:24px;text-align:center"><a href="${authUrl}" style="background:#22c55e;color:white;padding:12px 24px;border-radius:8px;text-decoration:none;font-weight:bold">Autorizar / Rechazar Cotización</a></div>` : ''}
</div>`;
};
